import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const vazio = { nome: "", email: "", perfil: "", pousada_ids: [], grupo_id: "", permissoes: [],
  conta_externa: false, conta_externa_produto: "", observacao: "" };

const toggle = (lista, v) => (lista.includes(v) ? lista.filter((x) => x !== v) : [...lista, v]);

function CheckList({ itens, selecionados, onChange, disabled, testid }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-stone-200 rounded-md p-3">
      {itens.map((i) => (
        <label key={i.id} className={`flex items-center gap-2 text-sm ${i.disabled || disabled ? "opacity-50" : "cursor-pointer"}`}>
          <Checkbox checked={selecionados.includes(i.id)} disabled={i.disabled || disabled}
            data-testid={`${testid}-${i.id}`}
            onCheckedChange={() => onChange(toggle(selecionados, i.id))} />
          {i.label || i.nome}
        </label>
      ))}
      {!itens.length && <div className="text-xs text-stone-500">Nenhuma opção disponível.</div>}
    </div>
  );
}

export function UsuarioDialog({ open, onOpenChange, meta, editing, onSaved }) {
  const [form, setForm] = useState(vazio);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { return; }
    setForm(editing ? { ...vazio, ...editing, grupo_id: editing.grupo_id || "",
      conta_externa_produto: editing.conta_externa_produto || "" } : vazio);
  }, [open, editing]);

  const perfilMeta = meta?.perfis.find((p) => p.id === form.perfil);
  const isGlobal = !!perfilMeta?.global;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const escolherPerfil = (perfil) => {
    const pm = meta.perfis.find((p) => p.id === perfil);
    set({ perfil, permissoes: pm?.permissoes_padrao.filter((m) => meta.modulos.find((x) => x.id === m)?.concedivel) || [] });
  };

  const salvar = async () => {
    if (!form.nome.trim()) return toast.error("Informe o nome completo");
    if (!editing && !form.email.trim()) return toast.error("Informe o e-mail");
    if (!form.perfil) return toast.error("Selecione o perfil");
    const payload = { nome: form.nome, perfil: form.perfil, permissoes: form.permissoes,
      pousada_ids: form.pousada_ids, grupo_id: form.grupo_id || null,
      conta_externa: form.conta_externa, conta_externa_produto: form.conta_externa_produto,
      observacao: form.observacao };
    if (editing?.protegido) { delete payload.perfil; }
    setBusy(true);
    try {
      if (editing) await api.patch(`/acessos/usuarios/${editing.id}`, payload);
      else await api.post("/acessos/usuarios", { ...payload, email: form.email });
      toast.success(editing ? "Usuário atualizado" : "Usuário cadastrado");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar");
    } finally { setBusy(false); }
  };

  if (!meta) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="usuario-dialog">
        <DialogHeader><DialogTitle>{editing ? "Editar usuário" : "Novo usuário"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Nome completo *</Label>
              <Input value={form.nome} data-testid="usuario-input-nome" onChange={(e) => set({ nome: e.target.value })} /></div>
            <div><Label>E-mail (conta Google) *</Label>
              <Input type="email" value={form.email} disabled={!!editing} data-testid="usuario-input-email"
                onChange={(e) => set({ email: e.target.value })} /></div>
          </div>
          <div>
            <Label>Perfil *</Label>
            <Select value={form.perfil} onValueChange={escolherPerfil} disabled={editing?.protegido}>
              <SelectTrigger data-testid="usuario-select-perfil"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                {meta.perfis.filter((p) => p.concedivel || p.id === form.perfil).map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`usuario-perfil-opt-${p.id}`}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {editing?.protegido && <p className="text-[11px] text-stone-500 mt-1">Conta da lista de segurança: o perfil não é alterado pela tela.</p>}
            {perfilMeta && !perfilMeta.global && (
              <p className="text-[11px] text-[#8B6A1F] mt-1" data-testid="usuario-aviso-unidade">
                Perfil por unidade: os módulos operacionais serão liberados somente após o isolamento por unidade.
              </p>
            )}
          </div>
          {form.perfil === "admin_grupo" && (
            <div>
              <Label>Grupo/cliente *</Label>
              <Select value={form.grupo_id} onValueChange={(v) => set({ grupo_id: v })}>
                <SelectTrigger data-testid="usuario-select-grupo"><SelectValue placeholder="Selecione o grupo" /></SelectTrigger>
                <SelectContent>{meta.grupos.map((g) => <SelectItem key={g.id} value={g.id}>{g.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {form.perfil && !isGlobal && form.perfil !== "admin_grupo" && (
            <div><Label>Unidades autorizadas *</Label>
              <CheckList itens={meta.pousadas} selecionados={form.pousada_ids} testid="usuario-unidade"
                onChange={(v) => set({ pousada_ids: v })} /></div>
          )}
          {isGlobal && <p className="text-xs text-stone-500">Perfil global: acesso a todas as unidades.</p>}
          {form.perfil && (
            <div><Label>Permissões por módulo</Label>
              <CheckList testid="usuario-perm" selecionados={form.permissoes} onChange={(v) => set({ permissoes: v })}
                disabled={form.perfil === "admin_geral"}
                itens={meta.modulos.map((m) => ({ ...m, disabled: !m.concedivel ||
                  (m.id === "usuarios" && !["admin_geral", "admin_grupo", "admin_pousada"].includes(form.perfil)) }))} />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <Switch checked={form.conta_externa} data-testid="usuario-switch-externa"
              onCheckedChange={(v) => set({ conta_externa: v })} />
            <span className="text-sm">Conta externa (outro produto ou pessoal)</span>
            {form.conta_externa && (
              <Input className="w-40" placeholder="Produto (ex.: IMJ)" value={form.conta_externa_produto}
                data-testid="usuario-input-externa-produto" onChange={(e) => set({ conta_externa_produto: e.target.value })} />
            )}
          </div>
          <div><Label>Observação</Label>
            <Textarea rows={2} value={form.observacao} data-testid="usuario-input-obs"
              onChange={(e) => set({ observacao: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="usuario-btn-cancelar">Cancelar</Button>
          <Button onClick={salvar} disabled={busy} data-testid="usuario-btn-salvar" className="bg-[#083A6B] hover:bg-[#062C52]">
            {busy ? "Salvando…" : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
